import type { DefaultError, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useDucatClient } from '@/context/DucatClientCtx';
import { useAppStore } from '@/stores/useAppStore';

import { QueryKeys } from '../query-keys';

import { getQuantaSignInIdentities } from './auth';
import { isQuantaHttpError } from './isQuantaHttpError';
import { quantaApi } from './quantaApi';
import { type QuantaIdentity, type QuantaIdentityRole, rememberQuantaIdentities, sameQuantaIdentity } from './quantaIdentity';

export interface QuantaCheckResponse {
  data: {
    isRegistered: boolean;
    role?: QuantaIdentityRole;
    identity?: QuantaIdentity;
    needsVaultLink?: boolean;
    vaultRegistered?: boolean;
    paymentRegistered?: boolean;
  };
}

type QueryOptionsParam = Omit<UseQueryOptions<QuantaCheckResponse, DefaultError>, 'queryKey' | 'queryFn'>;

export const useQuantaCheck = (options?: QueryOptionsParam): UseQueryResult<QuantaCheckResponse, Error> => {
  const { wallet, walletType, pubkey, walletAddress, accountRevision } = useDucatClient();

  return useQuery({
    queryKey: [QueryKeys.quantaCheck, pubkey, walletAddress, wallet?.account?.vault?.pubkey, accountRevision],
    queryFn: async () => {
      if (!wallet || !walletType) throw new Error('Connect a wallet to check your Quanta account.');
      const identities = await getQuantaSignInIdentities(walletType, wallet);
      if (!identities) throw new Error('The active wallet addresses could not be verified. Reconnect your wallet.');
      rememberQuantaIdentities(wallet, accountRevision, identities);
      const lookup = async (identity: QuantaIdentity): Promise<{ isRegistered: boolean; canSignIn?: boolean }> => {
        try {
          const result = await quantaApi.get(`/check?_t=${Date.now()}`, {
            headers: { 'x-user-public-key': identity.publicKey, 'x-address': identity.address },
          });
          if (typeof result.data?.data?.isRegistered !== 'boolean') {
            throw new Error('Invalid Quanta registration response.');
          }
          if (result.data.data.canSignIn !== undefined && typeof result.data.data.canSignIn !== 'boolean') {
            throw new Error('Invalid Quanta wallet verification response.');
          }
          return { isRegistered: result.data.data.isRegistered, canSignIn: result.data.data.canSignIn };
        } catch (error) {
          // Only the backend's structured missing-account response permits registration.
          if (
            isQuantaHttpError<QuantaCheckResponse>(error) &&
            error.response?.status === 404 &&
            error.response.data?.data?.isRegistered === false
          ) {
            return { isRegistered: false };
          }
          throw error;
        }
      };
      const requireUsable = (result: { isRegistered: boolean; canSignIn?: boolean }) => {
        if (result.isRegistered && result.canSignIn === false) {
          throw new Error('This wallet link needs verification. Sign in with the wallet that owns your Quanta account.');
        }
      };
      if (!identities.vault) {
        const payment = await lookup(identities.payment);
        requireUsable(payment);
        return {
          data: {
            ...payment,
            role: 'payment' as const,
            identity: identities.payment,
            needsVaultLink: false,
            paymentRegistered: payment.isRegistered,
          },
        };
      }
      const vault = await lookup(identities.vault);
      if (sameQuantaIdentity(identities.vault, identities.payment)) {
        requireUsable(vault);
        return { data: { ...vault, role: 'payment' as const, identity: identities.payment, needsVaultLink: false } };
      }
      const payment = await lookup(identities.payment);
      const paymentUsable = payment.isRegistered && payment.canSignIn !== false;
      const vaultUsable = vault.isRegistered && vault.canSignIn !== false;
      if (!paymentUsable && !vaultUsable) {
        requireUsable(payment);
        requireUsable(vault);
      }
      const headers = useAppStore.getState().quantaHeaders;
      // Keep an established legacy account until the user proves the payment
      // address belongs to it. Fresh sessions and registrations use the address
      // shown in the wallet header, with a fallback for vault-only accounts.
      const preserveVaultSession =
        headers && sameQuantaIdentity({ address: headers['x-address'], publicKey: headers['x-user-public-key'] }, identities.vault);
      const usePayment = !vaultUsable || (paymentUsable && !preserveVaultSession);
      const selected = usePayment ? payment : vault;
      return {
        data: {
          ...selected,
          role: (usePayment ? 'payment' : 'vault') as QuantaIdentityRole,
          identity: usePayment ? identities.payment : identities.vault,
          needsVaultLink: usePayment,
          vaultRegistered: vault.isRegistered,
          paymentRegistered: payment.isRegistered,
        },
      };
    },
    // Share a recent negative lookup across page/layout consumers and remounts.
    // Explicit Check again and post-registration invalidation still refresh immediately.
    staleTime: (query) => (query.state.data?.data.isRegistered ? Infinity : 30_000),
    refetchOnWindowFocus: false,
    retryOnMount: false,
    retry: false,
    enabled: !!pubkey,
    ...options,
  });
};
